import React, { useState, useEffect, useMemo, useRef } from "react";
import { getPdfPreviewById } from "../../services/proceedings.service.ts";

const DocumentsViewer = ({ fileData }) => {
  const cacheRef = useRef<Record<string, string>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const fileKeys = useMemo(
    () => (fileData || []).map((f) => `${f?.file?.id}:${f?.file?.extension || ''}`),
    [fileData]
  );

  useEffect(() => {
    let mounted = true;
    const fetchPreviews = async () => {
      const pending: Array<Promise<void>> = [];
      (fileData || []).forEach((file) => {
        const fid = file?.file?.id;
        if (!fid) return;
        if (cacheRef.current[fid]) return; 
        if (file?.file?.extension === 'pdf') {
          pending.push(
            (async () => {
              const url = await fetchPreview(fid);
              cacheRef.current[fid] = url;
            })()
          );
        } else {
          cacheRef.current[fid] = file.url;
        }
      });
      if (pending.length) await Promise.all(pending);
      if (mounted) setPreviewUrls({ ...cacheRef.current });
    };
    fetchPreviews();
    return () => { mounted = false; };
  }, [fileKeys]);

  const fetchPreview = async (fileId) => {
    const response = await getPdfPreviewById(fileId);
    return response?.url;
  };

  const openImagePreview = (url: string) => setImagePreviewUrl(url);
  const closeImagePreview = () => setImagePreviewUrl(null);

  const renderFile = (file, index) => {
    if (file?.file?.extension === "pdf") {
      const pdfUrl = previewUrls[file.file.id] || file.url;
      return (
        <div
          key={index}
          style={{
            width: `100%`,
            height: "100%",
            backgroundColor: "#ccc",
            borderRadius: "5px",
            position: "relative",
          }}
          className="d-flex justify-content-center align-items-center"
        >
          {pdfUrl ? (
            <>
            <object
                data={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <a
                  href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir PDF
              </a>
            </object>
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                }}
              >
                <button
                  type="button"
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "5px 10px",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const link = document.createElement("a");
                    link.href = pdfUrl;
                    link.download = `${file.file?.name || "documento"}.pdf`;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <i className="fa-solid fa-download"></i>
                </button>
              </div>
            </>
          ) : (
            <p>Loading...</p>
          )}
        </div>
      );
    } else {
      return (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            <img
              key={index}
              src={file.url}
              alt={file.name}
              className="img-fluid"
              style={{
                width: "100%",
                height: "100%",
                flex: 1,
                borderRadius: "5px",
                objectFit: "cover",
                backgroundColor: "#ccc",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                display: "flex",
                gap: "0.5rem",
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: "#111827",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openImagePreview(file.url);
                }}
                title="Ver imagen"
              >
                <i className="fa-solid fa-eye"></i>
              </button>
              <button
                type="button"
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(file.url, "_blank");
                }}
              >
                <i className="fa-solid fa-download"></i>
              </button>
            </div>
          </div>
        </>
      );
    }
  };

  return fileData.length === 1 ? (
    <div style={{ width: "100%", height: "100%" }}>
      {fileData.map((file, index) => renderFile(file, index))}
      {imagePreviewUrl && (
        <div
          onClick={closeImagePreview}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={closeImagePreview}
              style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                width: "32px",
                height: "32px",
                border: "none",
                borderRadius: "999px",
                backgroundColor: "#111827",
                color: "#fff",
                cursor: "pointer",
              }}
              title="Cerrar"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img
              src={imagePreviewUrl}
              alt="Vista previa"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: "8px",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          </div>
        </div>
      )}
    </div>
  ) : fileData.length === 2 ? (
    <React.Fragment>
      <div className="d-flex flex-row" style={{ width: "100%", height: "100%" }}>
        {renderFile(fileData[0], 0)}
        {renderFile(fileData[1], 1)}
      </div>
      {imagePreviewUrl && (
        <div
          onClick={closeImagePreview}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={closeImagePreview}
              style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                width: "32px",
                height: "32px",
                border: "none",
                borderRadius: "999px",
                backgroundColor: "#111827",
                color: "#fff",
                cursor: "pointer",
              }}
              title="Cerrar"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img
              src={imagePreviewUrl}
              alt="Vista previa"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: "8px",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  ) : fileData.length === 3 ? (
    <React.Fragment>
      <div className="d-flex flex-row" style={{ width: "100%", height: "100%" }}>
        {renderFile(fileData[0], 0)}
        <div
          className="d-flex flex-column"
          style={{ width: "100%", height: "100%" }}
        >
          <div className="h-50">{renderFile(fileData[1], 1)}</div>
          <div className="h-50">{renderFile(fileData[2], 2)}</div>
        </div>
      </div>
      {imagePreviewUrl && (
        <div
          onClick={closeImagePreview}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={closeImagePreview}
              style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                width: "32px",
                height: "32px",
                border: "none",
                borderRadius: "999px",
                backgroundColor: "#111827",
                color: "#fff",
                cursor: "pointer",
              }}
              title="Cerrar"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img
              src={imagePreviewUrl}
              alt="Vista previa"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: "8px",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  ) : (
    fileData.length > 3 && (
      <React.Fragment>
        <div className="d-flex flex-row" style={{ width: "100%", height: "100%" }}>
          {renderFile(fileData[0], 0)}
          <div
            className="d-flex flex-column"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="h-50">{renderFile(fileData[1], 1)}</div>
            <div className="h-50 bg-primary" style={{ position: "relative" }}>
              {renderFile(fileData[2], 2)}
              <div
                className="text-white fw-600 d-flex justify-content-center align-items-center"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: "100%",
                  height: "100%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                }}
              >
                + {fileData.length - 3} archivos
              </div>
              <button
                type="button"
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(fileData[2].url, "_blank");
                }}
              >
                <i className="fa-solid fa-download"></i>
              </button>
            </div>
          </div>
        </div>
        {imagePreviewUrl && (
          <div
            onClick={closeImagePreview}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              zIndex: 2000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh",
              }}
            >
              <button
                type="button"
                onClick={closeImagePreview}
                style={{
                  position: "absolute",
                  top: "-10px",
                  right: "-10px",
                  width: "32px",
                  height: "32px",
                  border: "none",
                  borderRadius: "999px",
                  backgroundColor: "#111827",
                  color: "#fff",
                  cursor: "pointer",
                }}
                title="Cerrar"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
              <img
                src={imagePreviewUrl}
                alt="Vista previa"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  borderRadius: "8px",
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              />
            </div>
          </div>
        )}
      </React.Fragment>
    )
  );
};

function areEqual(prev: any, next: any) {
  const a = (prev.fileData || []).map((f: any) => `${f?.file?.id}:${f?.file?.extension || ''}`);
  const b = (next.fileData || []).map((f: any) => `${f?.file?.id}:${f?.file?.extension || ''}`);
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export default React.memo(DocumentsViewer, areEqual);
